import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getApiSubscriptionTypesBySubscriptionTypeIdOptions } from 'api/generated/@tanstack/react-query.gen';
import type { OrderResponse } from 'api/generated/types.gen';
import type { SubscriptionLimits } from 'lib/helpers/subscription-limit-helpers';

/**
 * The rules the order's subscriptions were sold under — how many seats each has
 * to keep and how many events it has to cover — keyed by subscription type.
 * The order carries neither, so they are read off the types it names.
 *
 * Empty until the lookups answer, and for any that fail: the split enforces the
 * same rules, so a missing answer only defers a refusal rather than losing it.
 */
export const useSubscriptionLimits = (order: OrderResponse | undefined): Map<number, SubscriptionLimits> => {
  const subscriptionTypeIds = useMemo(() => Array.from(new Set((order?.subscriptionTypeGroups ?? []).map(group => group.subscriptionTypeId).filter((id): id is number => typeof id === 'number'))), [order]);
  const results = useQueries({
    queries: subscriptionTypeIds.map(subscriptionTypeId => ({
      ...getApiSubscriptionTypesBySubscriptionTypeIdOptions({
        path: {
          subscriptionTypeId
        },
        // Read through the order, so an off-sale subscription still answers.
        query: {
          encryptedOrderId: order?.encryptedId ?? undefined
        }
      }),
      // Set when the subscription goes on sale and not touched again, so one
      // read lasts the visit.
      staleTime: Infinity
    }))
  });

  // `useQueries` hands back a fresh array every render, so the map is rebuilt
  // only when a lookup has actually answered.
  const answered = results.map(result => result.dataUpdatedAt).join(',');
  return useMemo(() => new Map(subscriptionTypeIds.flatMap((subscriptionTypeId, index) => {
    const data = results[index]?.data;
    return data ? [[subscriptionTypeId, {
      minTickets: data.minTickets,
      minEvents: data.minEvents,
      allEventsRequired: data.allEventsRequired,
      pickPerEvent: data.pickPerEvent
    }] as const] : [];
  })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [subscriptionTypeIds, answered]);
};